import '@elevenlabs/convai-widget-embed'
import { businessBriefOptions, queryKeys, workspaceDetailedOptions } from '@doneverse/data/queries'
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { RefObject, useEffect, useMemo, useRef } from 'react'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'
import { COACH_GRACE_OPEN_EVENT_NAME, type CoachGraceOpenChatOptions } from '@/contexts/CoachGraceContext'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useToken } from '@/hooks/useToken'
import {
  buildCoachGraceDestinations,
  buildPlaybookDestinations,
  describeDestinations,
  findExactDestination,
  resolveCoachGraceDestination,
  type CoachGraceDestination,
} from '@/lib/coachGraceNavigation'
import { submitCoachGraceInitialMessage } from '@/lib/coachGraceWidget'
import { isRestrictedDoer } from '@/lib/doerAccess'

// Env-specific: the agent's server-tools (store_playbook_brief, MCP) point at a
// fixed API env, so staging MUST use the staging agent or briefs resolve against
// prod and fail auth (staging-issued token rejected by the prod API). Defaults to
// the prod agent so prod behaviour is unchanged when the override is unset.
const AGENT_ID =
  (import.meta.env.VITE_COACH_GRACE_AGENT_ID as string | undefined) ?? 'agent_0301khgtty55f8fbvc54af4n6y4t'
const WIDGET_TAG = 'elevenlabs-convai'
export const EXPAND_EVENT_NAME = 'elevenlabs-agent:expand'
const CALL_EVENT_NAME = 'elevenlabs-convai:call'
const BRIEF_SYNC_TOOL_NAME = 'brief_completed'
const NAVIGATE_TOOL_NAME = 'navigate_to_page'
// The width below which we stretch the chat sheet fullscreen (PHONE_FULLSCREEN_CSS
// below is scoped to it). Above it the sheet is a corner card and the page is
// visible beside it; below it the sheet covers everything, so a navigation the
// founder can't see reads as nothing having happened — which is why
// `navigate_to_page` also consults it before deciding whether to get out of the
// way while the route changes.
const PHONE_SHEET_MEDIA_QUERY = '(max-width: 767px)'
// Long enough for the founder to register the sheet leaving and the new page
// arriving, short enough that the conversation doesn't feel stalled. Paid twice
// per phone navigation (once before, once after), on top of the route change.
const PHONE_NAVIGATION_PAUSE_MS = 500
// Hosts whose links Grace's messages render as clickable. The widget turns each
// entry into an exact `https://<host>` origin (plus www.), so subdomains are NOT
// covered by `doneverse.com` and each one we link to has to be listed: `go.` is
// Refer a Friend, `thehub.` is the website template selector her knowledge base
// gives out. Zoom is the live support room.
const MARKDOWN_LINK_ALLOWED_HOSTS = [
  'zoom.us',
  'us02web.zoom.us',
  'doneverse.com',
  'go.doneverse.com',
  'thehub.doneverse.com',
  'dashboard.doneverse.com',
].join(',')
const DEFAULT_FIRST_MESSAGE =
  "Hi! I'm Grace, your startup coach. I can help you with your playbooks, business strategy, and anything else on your founder journey. What can I help you with today?"

type ElevenLabsCallConfig = {
  clientTools?: Record<string, (params: unknown) => unknown | Promise<unknown>>
} & Record<string, unknown>

type ElevenLabsCallEvent = CustomEvent<{ config: ElevenLabsCallConfig }>

// The ElevenLabs widget has no supported way to open its chat sheet
// fullscreen: the sheet always starts in the floating "compact" card and only
// an in-conversation toggle (hidden until a transcript exists) switches size —
// see WidgetSizeProvider/Sheet in @elevenlabs/convai-widget-core. The compact
// card looks broken on phones, so we adopt a stylesheet into the widget's
// open shadow root that stretches the sheet to the full viewport below the
// widget's own 768px mobile breakpoint, mirroring its data-variant=fullscreen
// styles. height/inset (not 100dvh) so the sheet tracks the WebView resize
// when the soft keyboard opens.
const PHONE_FULLSCREEN_CSS = `
@media ${PHONE_SHEET_MEDIA_QUERY} {
  /* Collapsed launcher: keep the widget's own corner margins, just lifted
     clear of the home indicator in the edge-to-edge WebView. */
  .overlay:not(:has(.sheet)) {
    margin-bottom: var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px));
  }
  /* Open chat: stretch the overlay to the full viewport and paint the
     status-bar strip above the sheet (the sheet itself starts below it;
     absolutely-positioned children like the floating avatar ignore sheet
     padding, so a top offset is used instead of padding-top). */
  .overlay:has(.sheet) {
    inset: 0 !important;
    background: var(--el-base, #fff);
  }
  .sheet {
    /* Edge-to-edge WebView: keep the sheet below the status bar. */
    inset: var(--safe-area-inset-top, 0px) 0 auto 0 !important;
    width: 100% !important;
    max-width: none !important;
    /* --vvh tracks the visual viewport (lib/viewport.ts) and inherits into
       the shadow root, so the sheet shrinks above the soft keyboard instead
       of leaving the composer hidden behind it. */
    height: calc(var(--vvh, 100%) - var(--safe-area-inset-top, 0px)) !important;
    max-height: none !important;
    margin: 0 !important;
    border-radius: 0 !important;
    /* Keep the composer clear of the home indicator. */
    padding-bottom: var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) !important;
  }
  /* The launcher pill doubles as the collapse chevron while the sheet is
     open; the fullscreen sheet (z-index: 2) would bury it bottom-right, so
     pin it above the sheet as a top-right close button. The sheet unmounts
     when collapsed, so :has(.sheet) only matches while the chat is open. */
  .overlay:has(.sheet) > div:not(.sheet) {
    position: absolute;
    top: calc(env(safe-area-inset-top, 0px) + 12px);
    right: 12px;
    z-index: 3;
  }
}
`

function setChatSheet(action: 'expand' | 'collapse') {
  document.dispatchEvent(new CustomEvent(EXPAND_EVENT_NAME, { detail: { action } }))
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

async function adoptPhoneFullscreenStyle(widget: HTMLElement) {
  await customElements.whenDefined(WIDGET_TAG)
  // The shadow root attaches when the element upgrades; give it a few frames.
  for (let attempt = 0; attempt < 10 && !widget.shadowRoot; attempt++) {
    await new Promise(requestAnimationFrame)
  }
  const root = widget.shadowRoot
  if (!root) return
  const sheet = new CSSStyleSheet()
  sheet.replaceSync(PHONE_FULLSCREEN_CSS)
  root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet]
}

/**
 * The founder's own playbooks as navigable pages. Reads through the query cache,
 * so the usual case — the workspace was already fetched by a page they visited —
 * costs nothing.
 *
 * A workspace we can't read yields no playbook destinations rather than an
 * error: the menu still resolves on its own, so a failed fetch degrades to the
 * menu-only behaviour instead of refusing every request.
 */
async function fetchPlaybookDestinations(queryClient: QueryClient): Promise<CoachGraceDestination[]> {
  try {
    const workspace = await queryClient.fetchQuery(workspaceDetailedOptions())
    return buildPlaybookDestinations(workspace?.playbooks ?? [])
  } catch {
    return []
  }
}

function setOptionalAttribute(widget: HTMLElement, name: string, value?: string) {
  if (value && value.trim().length > 0) {
    widget.setAttribute(name, value)
    return
  }
  widget.removeAttribute(name)
}

function setOptionalBooleanAttribute(widget: HTMLElement, name: string, value?: boolean) {
  if (typeof value === 'boolean') {
    widget.setAttribute(name, String(value))
    return
  }
  widget.removeAttribute(name)
}

export interface UseElevenLabsWidgetOptions {
  playbookId?: string
  playbookName?: string
  playbookType?: string
  playbookQuestions?: string
  playbookBriefId?: string
  playbookBriefStatus?: string
  workspacePlaybookStatus?: string
  playbookBriefAnswers?: string
  playbookAnsweredQuestionIds?: string
  overrideFirstMessage?: string
}

export function useElevenLabsWidget(
  containerRef: RefObject<HTMLDivElement | null>,
  shouldShow: boolean,
  /**
   * Changing this tears the embedded widget down and builds a fresh one, ending
   * any conversation in progress. Reserve it for cases that genuinely need the
   * widget's teardown path (releasing the microphone) — context changes do not,
   * since the effects below push them onto the live element's attributes.
   */
  resetKey: string,
  options?: UseElevenLabsWidgetOptions,
) {
  const { user, profile } = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { hasFlag } = useFeatureFlags()
  const widgetRef = useRef<HTMLElement | null>(null)
  const token = useToken()

  // Everything Grace may open, derived from this user's own menu so she can
  // never route them somewhere the sidebar hides and the router would bounce.
  // Deliberately NOT sent to the agent as context: she asks for a page by the
  // name she already uses for it, and only learns the list if she asks for one
  // that doesn't exist (see navigateToPage below).
  const role = profile?.role
  const restrictedDoer = isRestrictedDoer(profile, hasFlag)
  const destinations = useMemo(
    () => buildCoachGraceDestinations(role, hasFlag, restrictedDoer),
    [hasFlag, restrictedDoer, role],
  )

  const { data: businessBrief } = useQuery({
    ...businessBriefOptions(),
    enabled: shouldShow,
  })

  const playbookId = options?.playbookId ?? ''
  const playbookName = options?.playbookName ?? 'no playbook'
  const playbookType = options?.playbookType ?? ''
  const playbookQuestions = options?.playbookQuestions ?? '[]'
  const playbookBriefId = options?.playbookBriefId ?? ''
  const playbookBriefStatus = options?.playbookBriefStatus ?? ''
  const workspacePlaybookStatus = options?.workspacePlaybookStatus ?? ''
  const playbookBriefAnswers = options?.playbookBriefAnswers ?? '{}'
  const playbookAnsweredQuestionIds = options?.playbookAnsweredQuestionIds ?? '[]'
  const overrideFirstMessage = options?.overrideFirstMessage
  const hasConversationHistory = profile?.coach_grace?.has_conversation_history ?? false

  const baseDynamicVariables = useMemo(() => {
    const userId = user?.id ?? user?.sub ?? ''

    const vars: Record<string, string> = {
      user_id: userId,
      business_brief: JSON.stringify(businessBrief?.data ?? {}),
      has_conversation_history: String(hasConversationHistory),
      selected_playbook_id: playbookId,
      selected_playbook_name: playbookName,
      selected_playbook_type: playbookType,
      selected_playbook_questions: playbookQuestions,
      selected_playbook_brief_id: playbookBriefId,
      selected_playbook_brief_status: playbookBriefStatus,
      selected_workspace_playbook_status: workspacePlaybookStatus,
      selected_playbook_answers: playbookBriefAnswers,
      selected_playbook_answered_question_ids: playbookAnsweredQuestionIds,
      // Must be present, and empty is the correct value.
      //
      // The Doneverse MCP resource declares an assignment on store-playbook-brief
      // (brief_record_id <- data.playbook_id). ElevenLabs treats a declared
      // dynamic variable as one that must EXIST when the session opens, so
      // omitting it does not degrade gracefully — the socket closes and the
      // conversation ends `failed | Missing required dynamic variables in
      // tools: {'brief_record_id'}`.
      //
      // It only fires the moment a brief is submitted, which is why chat looked
      // healthy while briefing was not: on production, the three most recent
      // conversations that reached store-playbook-brief ALL died this way
      // (19, 21 and 24 Aug), and no brief has been written since. The agent
      // test harness sets this variable itself, so every e2e passed throughout.
      //
      // The agent fills the real value from the tool response; we only have to
      // declare it.
      brief_record_id: '',
    }
    if (token) {
      vars.secret__relay_jwt = `Bearer ${token}`
    }
    return vars
  }, [
    businessBrief,
    hasConversationHistory,
    playbookAnsweredQuestionIds,
    playbookBriefAnswers,
    playbookBriefId,
    playbookBriefStatus,
    playbookId,
    playbookName,
    playbookQuestions,
    playbookType,
    token,
    user?.id,
    user?.sub,
    workspacePlaybookStatus,
  ])

  // Store latest dynamic variables in a ref so the creation effect doesn't
  // re-run (and destroy the widget) when variables change.
  const dynamicVarsRef = useRef(baseDynamicVariables)
  dynamicVarsRef.current = baseDynamicVariables

  // Same reason: the client tools are handed to the widget once, when the call
  // starts, and must read the destination list as it is at the moment the agent
  // calls the tool — not as it was when the listener was attached.
  const destinationsRef = useRef(destinations)
  destinationsRef.current = destinations

  useEffect(() => {
    if (!shouldShow) return

    const container = containerRef.current
    if (!container) return

    const widget = document.createElement(WIDGET_TAG)
    widget.setAttribute('agent-id', AGENT_ID)
    widget.setAttribute('dynamic-variables', JSON.stringify(dynamicVarsRef.current))
    widget.setAttribute('override-first-message', DEFAULT_FIRST_MESSAGE)
    widget.setAttribute('markdown-link-allowed-hosts', MARKDOWN_LINK_ALLOWED_HOSTS)
    container.replaceChildren(widget)
    void adoptPhoneFullscreenStyle(widget)
    widgetRef.current = widget

    return () => {
      if (container.contains(widget)) {
        container.removeChild(widget)
      }
      widgetRef.current = null
    }
  }, [containerRef, resetKey, shouldShow])

  useEffect(() => {
    const widget = widgetRef.current
    if (!widget) return
    widget.setAttribute('dynamic-variables', JSON.stringify(baseDynamicVariables))
  }, [baseDynamicVariables])

  useEffect(() => {
    const widget = widgetRef.current
    if (!widget) return
    widget.setAttribute('override-first-message', overrideFirstMessage || DEFAULT_FIRST_MESSAGE)
  }, [overrideFirstMessage])

  useEffect(() => {
    if (!shouldShow) return

    const collapseChatAndRefetchBrief = async () => {
      setChatSheet('collapse')

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.workspace }),
        queryClient.invalidateQueries({ queryKey: queryKeys.playbooks }),
      ])

      toast.success('Brief submitted!', { description: 'Coach Grace AI saved your answers.' })
    }

    // A doneverse.com link opens in a new tab, as it does from the sidebar, so
    // the Dashboard and the conversation stay where they are.
    //
    // The tab can be refused: this runs when the agent's tool call arrives, not
    // on a click, and a browser may treat that as an unrequested popup. When it
    // does, the URL goes back to Grace so she can hand the founder a link to
    // click — doneverse.com hosts are clickable in her messages. The URL is only
    // returned on that path; a tab that opened needs nothing said about it.
    const openLink = (label: string, href: string): string => {
      const opened = window.open(href, '_blank')
      if (!opened) {
        return (
          `The browser blocked opening ${label} in a new tab. ` +
          `Give the founder this link to click instead: ${href}`
        )
      }
      // `noopener` in the features string would make window.open return null
      // even on success, hiding a block; cut the opener here instead.
      opened.opener = null
      return `Opened ${label} in a new tab.`
    }

    // Takes the founder to a page they asked for out loud.
    //
    // The miss branch is not error handling — it is how the agent discovers what
    // exists. Grace is never handed the page list as context (it would be prompt
    // weight on every conversation, most of which never navigate, and a
    // per-user dynamic variable every conversation harness would then have to
    // supply). She asks for a page by the name she already uses for it; if that
    // name isn't real, the return value tells her what is, and she calls again.
    const openPage = async (target: CoachGraceDestination): Promise<string> => {
      if (target.href) return openLink(target.label, target.href)
      if (!target.to) return `There is no page called "${target.label}".`

      // On a phone the sheet covers the whole viewport, so a route change behind
      // it reads as nothing having happened. Step out of the way first, let the
      // founder watch the page arrive, then come back — with a beat on either
      // side so the three moves read as a sequence rather than a flicker. The
      // widget stays mounted throughout, so the conversation carries on.
      if (window.matchMedia?.(PHONE_SHEET_MEDIA_QUERY).matches) {
        setChatSheet('collapse')
        await wait(PHONE_NAVIGATION_PAUSE_MS)
        await navigate({ to: target.to })
        await wait(PHONE_NAVIGATION_PAUSE_MS)
        setChatSheet('expand')
      } else {
        await navigate({ to: target.to })
      }

      // Label only, no path — the agent reads tool results, and a URL in there
      // is a URL it might say out loud.
      return `Opened ${target.label}.`
    }

    const navigateToPage = async (params: unknown): Promise<string> => {
      const requested =
        typeof (params as { page?: unknown } | null)?.page === 'string'
          ? (params as { page: string }).page
          : ''
      const menu = destinationsRef.current

      // An exact page name is safe to act on straight away, and keeps the
      // common request off the network.
      const exact = findExactDestination(menu, requested)
      if (exact) return openPage(exact)

      // Anything looser has to be weighed against everything the founder could
      // have meant, playbooks included — resolving inside the menu alone would
      // let "launch" pick Launch Checklist over their New Online Course Launch
      // playbook without ever noticing there were two.
      const match = resolveCoachGraceDestination(
        [...menu, ...(await fetchPlaybookDestinations(queryClient))],
        requested,
      )

      if (match.status === 'found') return openPage(match.destination)

      if (match.status === 'ambiguous') {
        return (
          `"${requested}" could mean any of: ${describeDestinations(match.candidates)}. ` +
          `Ask the founder which one they want, then call this again with that name.`
        )
      }

      return (
        `There is no page called "${requested}". The pages you can open are: ` +
        `${describeDestinations(menu)}. You can also pass one of the founder's ` +
        `playbook names. Call this again with one of those, or tell the founder ` +
        `you can't open that one.`
      )
    }

    const handleCallEvent = (event: Event) => {
      const widget = widgetRef.current
      if (!widget || event.target !== widget) return

      const { detail } = event as ElevenLabsCallEvent
      if (!detail?.config) return

      const existingClientTools = detail.config.clientTools ?? {}
      const doneverseClientTools: Record<string, (params: unknown) => Promise<string>> = {
        [BRIEF_SYNC_TOOL_NAME]: async () => {
          await collapseChatAndRefetchBrief()
          return 'Closed chat and triggered brief refetch.'
        },
        [NAVIGATE_TOOL_NAME]: navigateToPage,
      }

      detail.config.clientTools = {
        ...existingClientTools,
        ...doneverseClientTools,
      }
    }

    document.addEventListener(CALL_EVENT_NAME, handleCallEvent as EventListener)

    return () => {
      document.removeEventListener(CALL_EVENT_NAME, handleCallEvent as EventListener)
    }
  }, [navigate, queryClient, shouldShow])

  useEffect(() => {
    if (!shouldShow) return

    const handleOpenChat = (event: Event) => {
      const widget = widgetRef.current
      if (!widget) return

      const { detail } = event as CustomEvent<CoachGraceOpenChatOptions>
      const options = detail ?? {}

      const dynamicVariables: Record<string, string> = {
        ...baseDynamicVariables,
        ...(options.dynamicVariables ?? {}),
      }

      widget.setAttribute('dynamic-variables', JSON.stringify(dynamicVariables))
      setOptionalAttribute(widget, 'override-prompt', options.overridePrompt)
      widget.setAttribute('override-first-message', options.overrideFirstMessage ?? DEFAULT_FIRST_MESSAGE)
      setOptionalBooleanAttribute(widget, 'override-text-only', options.overrideTextOnly)

      setChatSheet('expand')

      if (options.initialUserMessage) {
        void submitCoachGraceInitialMessage(widget, options.initialUserMessage)
      }
    }

    document.addEventListener(COACH_GRACE_OPEN_EVENT_NAME, handleOpenChat as EventListener)

    return () => {
      document.removeEventListener(COACH_GRACE_OPEN_EVENT_NAME, handleOpenChat as EventListener)
    }
  }, [baseDynamicVariables, shouldShow])
}
